import {
  accountScopedStorageKey,
  readAccountScopedStorage,
  removeAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  briefProfileCollectionFromAndroidSyncPreferences,
  briefProfileFromAndroidSyncPreferences,
  briefProfilesJsonForAndroid,
  loadBriefProfileCollection,
  normalizeBriefProfile,
  saveBriefProfile,
  saveBriefProfileCollection,
} from "@/shared/brief/run-store";
import { migrateBriefProfileCollectionForCountryChange } from "@/shared/brief/styles";
import {
  homeCarouselSettingsFromVisibleIds,
  migrateExplicitHomeCarouselDeckForCountry,
  normalizeHomeCarouselSettings,
  saveHomeCarouselSettings,
} from "@/shared/home-carousel/carousel-store";
import { readWebAccountSettings, withWebAccountSettings } from "./account-data-sync.mjs";
import {
  hasCanonicalHiddenDefaultStocksField,
  preferenceStoragePayload,
} from "./preference-storage-contract.mjs";
import {
  migrateUnavailableNewsSource,
  marketRegionPreferenceTransition,
  normalizeMarketCountry,
  normalizeMarketLanguage,
} from "./market-region-transition.mjs";
import {
  decodePortableBriefCursor,
  mergePortableBriefCursors,
} from "@/shared/brief/portable-reading-cursor.mjs";

const PREFS_KEY = "tb_web_prefs_v1";
// The v2 cursor epoch matches the isolated task/article row stores introduced
// in July 2026. Reusing the v1 cursor skipped the one full pull those new
// stores needed, leaving older account rows permanently invisible on web.
const SYNC_SINCE_KEY = "tb_sync_last_pull_v2";
const PREFERENCES_EVENT = "tb-public-web-preferences-changed";
const HOME_CAROUSEL_KEY = "tb_public_web_home_carousel_v1";
const HOME_CAROUSEL_EVENT = "tb-public-web-home-carousel-changed";

function normalizedCoordinateString(value: unknown, min: number, max: number): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) && numeric >= min && numeric <= max ? trimmed : "";
}

function normalizedOnboardingInterestTags(value: unknown): string {
  if (typeof value !== "string") return "";
  return [
    ...new Set(
      value
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean),
    ),
  ]
    .sort()
    .join(",");
}

function loadExplicitHomeCarouselSettingsForSync(country: string) {
  let raw: string | null;
  try {
    raw = readAccountScopedStorage(HOME_CAROUSEL_KEY);
  } catch {
    return null;
  }
  if (raw == null) return null;
  try {
    return normalizeHomeCarouselSettings(JSON.parse(raw), country);
  } catch {
    // A corrupt-but-present row is still an explicit membership boundary.
    return normalizeHomeCarouselSettings({ order: [], hidden: {} }, country);
  }
}

function clearHomeCarouselCustomizationFromSync(country: string) {
  try {
    removeAccountScopedStorage(HOME_CAROUSEL_KEY);
  } catch {
    /* the resolved default remains safe even if storage is unavailable */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(HOME_CAROUSEL_EVENT, {
        detail: normalizeHomeCarouselSettings(null, country),
      }),
    );
  }
}

export type WebPreferences = {
  lang: string;
  newsSource: string;
  country: string;
  preferredDisplayName: string;
  homePlaceId: string;
  workPlaceId: string;
  homePlaceLabel: string;
  workPlaceLabel: string;
  homePlaceVerifiedAtEpochMs: number;
  workPlaceVerifiedAtEpochMs: number;
  homePlaceNeedsReselection: boolean;
  workPlaceNeedsReselection: boolean;
  weatherLatitude: string;
  weatherLongitude: string;
  weatherLocationLabel: string;
  /** Opaque Android `weather_custom_slots_v1` encoding; preserve losslessly for sync. */
  weatherCustomSlots: string;
  carouselAutoRotate: boolean;
  audioPlaybackSpeed: number;
  audioVoiceId: string;
  morningBriefEnabled: boolean;
  morningBriefHour: number;
  morningBriefQuietHoursEnabled: boolean;
  /** Android-canonical lowercase CSV of hidden News category section ids. */
  hiddenNewsCategories: string;
  /** Android-canonical lowercase CSV of hidden News publisher source ids. */
  hiddenNewsPublishers: string;
  /** Android-canonical lowercase CSV of account-owned onboarding interests. */
  onboardingInterestTags: string;
  /** Android-canonical CSV of country-default stocks removed from the effective watchlist. */
  hiddenDefaultStockSymbols: string;
  localCityLabel: string;
  localNewsUserCityCatalogKey: string;
  localNewsGpsSuggestedCatalogKey: string;
  localNewsCityUseDeviceLocation: boolean;
  /**
   * Legacy browser-local compatibility field. Public web does not schedule or
   * deliver Daily Mastery notifications, and this must never enter cloud sync.
   */
  dailyMasteryReminderEnabled: boolean;
  newsBriefPrimarySource: string;
  newsBriefSecondarySource: string;
  /** Raw canonical Android catalog retained so web edits do not delete inactive profiles. */
  briefProfilesJson: string;
  activeBriefProfileId: string;
  briefClassicProgressJson: string;
  briefReelProgressJson: string;
  homeBriefReadingMode: "classic" | "reels";
};

const DEFAULTS: WebPreferences = {
  lang: "en",
  newsSource: "category-global-us-national",
  country: "US",
  preferredDisplayName: "",
  homePlaceId: "",
  workPlaceId: "",
  homePlaceLabel: "Home",
  workPlaceLabel: "Work",
  homePlaceVerifiedAtEpochMs: 0,
  workPlaceVerifiedAtEpochMs: 0,
  homePlaceNeedsReselection: false,
  workPlaceNeedsReselection: false,
  weatherLatitude: "",
  weatherLongitude: "",
  weatherLocationLabel: "",
  weatherCustomSlots: "",
  carouselAutoRotate: false,
  audioPlaybackSpeed: 1,
  audioVoiceId: "auto",
  morningBriefEnabled: true,
  morningBriefHour: 7,
  morningBriefQuietHoursEnabled: true,
  hiddenNewsCategories: "",
  hiddenNewsPublishers: "",
  onboardingInterestTags: "",
  hiddenDefaultStockSymbols: "",
  localCityLabel: "",
  localNewsUserCityCatalogKey: "",
  localNewsGpsSuggestedCatalogKey: "",
  localNewsCityUseDeviceLocation: true,
  dailyMasteryReminderEnabled: false,
  newsBriefPrimarySource: "category-global-us-national",
  newsBriefSecondarySource: "category-global-us-business",
  briefProfilesJson: "",
  activeBriefProfileId: "",
  briefClassicProgressJson: "",
  briefReelProgressJson: "",
  homeBriefReadingMode: "classic",
};

function normalizePreferences(raw: unknown): WebPreferences {
  const parsed = raw && typeof raw === "object" ? (raw as Partial<WebPreferences>) : {};
  const country = normalizeMarketCountry(parsed.country);
  const lang = normalizeMarketLanguage(parsed.lang, country);
  return {
    ...DEFAULTS,
    lang,
    newsSource: migrateUnavailableNewsSource(parsed.newsSource, country),
    country,
    preferredDisplayName:
      typeof parsed.preferredDisplayName === "string" ? parsed.preferredDisplayName : "",
    homePlaceId: typeof parsed.homePlaceId === "string" ? parsed.homePlaceId : "",
    workPlaceId: typeof parsed.workPlaceId === "string" ? parsed.workPlaceId : "",
    homePlaceLabel:
      typeof parsed.homePlaceLabel === "string" && parsed.homePlaceLabel.trim()
        ? parsed.homePlaceLabel
        : "Home",
    workPlaceLabel:
      typeof parsed.workPlaceLabel === "string" && parsed.workPlaceLabel.trim()
        ? parsed.workPlaceLabel
        : "Work",
    homePlaceVerifiedAtEpochMs:
      typeof parsed.homePlaceVerifiedAtEpochMs === "number" ? parsed.homePlaceVerifiedAtEpochMs : 0,
    workPlaceVerifiedAtEpochMs:
      typeof parsed.workPlaceVerifiedAtEpochMs === "number" ? parsed.workPlaceVerifiedAtEpochMs : 0,
    homePlaceNeedsReselection: parsed.homePlaceNeedsReselection === true,
    workPlaceNeedsReselection: parsed.workPlaceNeedsReselection === true,
    weatherLatitude: normalizedCoordinateString(parsed.weatherLatitude, -90, 90),
    weatherLongitude: normalizedCoordinateString(parsed.weatherLongitude, -180, 180),
    weatherLocationLabel:
      typeof parsed.weatherLocationLabel === "string" ? parsed.weatherLocationLabel.trim() : "",
    weatherCustomSlots:
      typeof parsed.weatherCustomSlots === "string" ? parsed.weatherCustomSlots : "",
    carouselAutoRotate:
      typeof parsed.carouselAutoRotate === "boolean"
        ? parsed.carouselAutoRotate
        : DEFAULTS.carouselAutoRotate,
    audioPlaybackSpeed:
      typeof parsed.audioPlaybackSpeed === "number" && Number.isFinite(parsed.audioPlaybackSpeed)
        ? Math.min(3, Math.max(0.75, parsed.audioPlaybackSpeed))
        : DEFAULTS.audioPlaybackSpeed,
    audioVoiceId:
      typeof parsed.audioVoiceId === "string" &&
      ["auto", "leda", "aoede", "charon", "orus"].includes(parsed.audioVoiceId)
        ? parsed.audioVoiceId
        : DEFAULTS.audioVoiceId,
    morningBriefEnabled:
      typeof parsed.morningBriefEnabled === "boolean"
        ? parsed.morningBriefEnabled
        : DEFAULTS.morningBriefEnabled,
    morningBriefHour:
      typeof parsed.morningBriefHour === "number"
        ? parsed.morningBriefHour
        : DEFAULTS.morningBriefHour,
    morningBriefQuietHoursEnabled:
      typeof parsed.morningBriefQuietHoursEnabled === "boolean"
        ? parsed.morningBriefQuietHoursEnabled
        : DEFAULTS.morningBriefQuietHoursEnabled,
    hiddenNewsCategories:
      typeof parsed.hiddenNewsCategories === "string" ? parsed.hiddenNewsCategories.trim() : "",
    hiddenNewsPublishers:
      typeof parsed.hiddenNewsPublishers === "string" ? parsed.hiddenNewsPublishers.trim() : "",
    onboardingInterestTags: normalizedOnboardingInterestTags(parsed.onboardingInterestTags),
    hiddenDefaultStockSymbols:
      typeof parsed.hiddenDefaultStockSymbols === "string"
        ? parsed.hiddenDefaultStockSymbols.trim()
        : "",
    localCityLabel: typeof parsed.localCityLabel === "string" ? parsed.localCityLabel.trim() : "",
    localNewsUserCityCatalogKey:
      typeof parsed.localNewsUserCityCatalogKey === "string"
        ? parsed.localNewsUserCityCatalogKey.trim()
        : "",
    localNewsGpsSuggestedCatalogKey:
      typeof parsed.localNewsGpsSuggestedCatalogKey === "string"
        ? parsed.localNewsGpsSuggestedCatalogKey.trim()
        : "",
    localNewsCityUseDeviceLocation:
      typeof parsed.localNewsCityUseDeviceLocation === "boolean"
        ? parsed.localNewsCityUseDeviceLocation
        : DEFAULTS.localNewsCityUseDeviceLocation,
    dailyMasteryReminderEnabled:
      typeof parsed.dailyMasteryReminderEnabled === "boolean"
        ? parsed.dailyMasteryReminderEnabled
        : DEFAULTS.dailyMasteryReminderEnabled,
    newsBriefPrimarySource: migrateUnavailableNewsSource(parsed.newsBriefPrimarySource, country),
    newsBriefSecondarySource:
      typeof parsed.newsBriefSecondarySource === "string" && parsed.newsBriefSecondarySource.trim()
        ? migrateUnavailableNewsSource(parsed.newsBriefSecondarySource, country)
        : DEFAULTS.newsBriefSecondarySource,
    briefProfilesJson: typeof parsed.briefProfilesJson === "string" ? parsed.briefProfilesJson : "",
    briefClassicProgressJson: mergePortableBriefCursors(
      parsed.briefClassicProgressJson,
      null,
      "classic",
    ),
    briefReelProgressJson: mergePortableBriefCursors(parsed.briefReelProgressJson, null, "reels"),
    homeBriefReadingMode: parsed.homeBriefReadingMode === "reels" ? "reels" : "classic",
    activeBriefProfileId:
      typeof parsed.activeBriefProfileId === "string" ? parsed.activeBriefProfileId.trim() : "",
  };
}

export function loadPreferences(): WebPreferences {
  try {
    const raw = readAccountScopedStorage(PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    return normalizePreferences(JSON.parse(raw));
  } catch {
    return { ...DEFAULTS };
  }
}

export function hasStoredPreferences(): boolean {
  try {
    return readAccountScopedStorage(PREFS_KEY) != null;
  } catch {
    return false;
  }
}

export function hasStoredHiddenDefaultStockSymbolsPreference(): boolean {
  try {
    const raw = readAccountScopedStorage(PREFS_KEY);
    return hasCanonicalHiddenDefaultStocksField(raw);
  } catch {
    return false;
  }
}

export function savePreferences(
  prefs: WebPreferences,
  options: { persistHiddenDefaultStocks?: boolean } = {},
) {
  const sanitized = normalizePreferences(prefs);
  const currentRaw = readAccountScopedStorage(PREFS_KEY);
  const payload = preferenceStoragePayload(sanitized, currentRaw, options);
  writeAccountScopedStorage(PREFS_KEY, JSON.stringify(payload));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PREFERENCES_EVENT, { detail: { preferences: sanitized } }),
    );
  }
}

export function onPreferencesChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    try {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key === null || event.key === accountScopedStorageKey(PREFS_KEY)) listener();
    } catch {
      // Storage denial or an unreadable auth scope must not break other listeners.
    }
  };
  window.addEventListener(PREFERENCES_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(PREFERENCES_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function preferencesToSyncPayload(prefs: WebPreferences) {
  prefs = normalizePreferences(prefs);
  const briefProfiles = loadBriefProfileCollection(prefs.country);
  const activeBriefProfile =
    briefProfiles.profiles.find((profile) => profile.id === briefProfiles.activeProfileId) ||
    briefProfiles.profiles[0];
  return withWebAccountSettings(
    {
      newsLang: prefs.lang,
      newsSource: prefs.newsSource,
      country: prefs.country,
      marketCountry: prefs.country,
      homePlaceId: prefs.homePlaceId,
      homePlaceLabel: prefs.homePlaceLabel,
      homePlaceVerifiedAtEpochMs: prefs.homePlaceVerifiedAtEpochMs,
      homePlaceNeedsReselection: prefs.homePlaceNeedsReselection,
      workPlaceId: prefs.workPlaceId,
      workPlaceLabel: prefs.workPlaceLabel,
      workPlaceVerifiedAtEpochMs: prefs.workPlaceVerifiedAtEpochMs,
      workPlaceNeedsReselection: prefs.workPlaceNeedsReselection,
      weatherLatitude: prefs.weatherLatitude,
      weatherLongitude: prefs.weatherLongitude,
      weatherLocationLabel: prefs.weatherLocationLabel,
      weatherCustomSlots: prefs.weatherCustomSlots,
      preferredDisplayName: prefs.preferredDisplayName,
      carouselAutoRotate: prefs.carouselAutoRotate,
      audioPlaybackSpeed: prefs.audioPlaybackSpeed,
      audioVoiceId: prefs.audioVoiceId,
      morningBriefEnabled: prefs.morningBriefEnabled,
      morningBriefHour: prefs.morningBriefHour,
      morningBriefQuietHoursEnabled: prefs.morningBriefQuietHoursEnabled,
      hiddenNewsCategories: prefs.hiddenNewsCategories,
      hiddenNewsPublishers: prefs.hiddenNewsPublishers,
      onboardingInterestTags: prefs.onboardingInterestTags,
      hiddenDefaultStockSymbols: prefs.hiddenDefaultStockSymbols,
      localCityLabel: prefs.localCityLabel,
      localNewsUserCityCatalogKey: prefs.localNewsUserCityCatalogKey,
      localNewsGpsSuggestedCatalogKey: prefs.localNewsGpsSuggestedCatalogKey,
      localNewsCityUseDeviceLocation: prefs.localNewsCityUseDeviceLocation,
      webNewsBriefPrimary: prefs.newsBriefPrimarySource,
      webNewsBriefSecondary: prefs.newsBriefSecondarySource,
      briefProfilesJson: briefProfilesJsonForAndroid(briefProfiles),
      activeBriefProfileId: briefProfiles.activeProfileId,
      ...(decodePortableBriefCursor(prefs.briefClassicProgressJson, "classic")
        ? { briefClassicProgressJson: prefs.briefClassicProgressJson }
        : {}),
      ...(decodePortableBriefCursor(prefs.briefReelProgressJson, "reels")
        ? { briefReelProgressJson: prefs.briefReelProgressJson }
        : {}),
      homeBriefReadingMode: prefs.homeBriefReadingMode,
    },
    activeBriefProfile,
    loadExplicitHomeCarouselSettingsForSync(prefs.country),
  );
}

export function applySyncPreferences(
  prefs: Record<string, unknown> | null,
  options: { remoteOwnedKeys?: string[] } = {},
) {
  if (!prefs || typeof prefs !== "object") return loadPreferences();
  const current = loadPreferences();
  const previousCountry = current.country;
  const next: WebPreferences = { ...current };
  const accountSettings = readWebAccountSettings(prefs);
  const owns = (key: string) => Object.prototype.hasOwnProperty.call(prefs, key);
  const remoteOwnedKeys = options.remoteOwnedKeys ? new Set(options.remoteOwnedKeys) : null;
  const remoteOwns = (key: string) => remoteOwnedKeys?.has(key) ?? owns(key);
  for (const [key, mode] of [
    ["briefClassicProgressJson", "classic"],
    ["briefReelProgressJson", "reels"],
  ] as const) {
    if (owns(key)) next[key] = mergePortableBriefCursors(current[key], prefs[key], mode);
  }
  if (
    owns("homeBriefReadingMode") &&
    (prefs.homeBriefReadingMode === "classic" || prefs.homeBriefReadingMode === "reels")
  ) {
    next.homeBriefReadingMode = prefs.homeBriefReadingMode;
  }
  if (owns("newsLang")) {
    next.lang = typeof prefs.newsLang === "string" ? prefs.newsLang : current.lang;
  }
  if (owns("newsSource")) {
    next.newsSource = typeof prefs.newsSource === "string" ? prefs.newsSource : "";
  }
  if (owns("marketCountry")) {
    next.country = accountSettings.marketCountry || current.country;
  } else if (owns("country")) {
    next.country = typeof prefs.country === "string" ? prefs.country : current.country;
  }
  if (owns("homePlaceId")) {
    next.homePlaceId = typeof prefs.homePlaceId === "string" ? prefs.homePlaceId : "";
  }
  if (owns("homePlaceLabel")) {
    next.homePlaceLabel =
      typeof prefs.homePlaceLabel === "string" && prefs.homePlaceLabel
        ? prefs.homePlaceLabel
        : DEFAULTS.homePlaceLabel;
  }
  if (owns("homePlaceVerifiedAtEpochMs")) {
    next.homePlaceVerifiedAtEpochMs =
      typeof prefs.homePlaceVerifiedAtEpochMs === "number" ? prefs.homePlaceVerifiedAtEpochMs : 0;
  }
  if (owns("homePlaceNeedsReselection")) {
    next.homePlaceNeedsReselection = prefs.homePlaceNeedsReselection === true;
  }
  if (owns("workPlaceId")) {
    next.workPlaceId = typeof prefs.workPlaceId === "string" ? prefs.workPlaceId : "";
  }
  if (owns("workPlaceLabel")) {
    next.workPlaceLabel =
      typeof prefs.workPlaceLabel === "string" && prefs.workPlaceLabel
        ? prefs.workPlaceLabel
        : DEFAULTS.workPlaceLabel;
  }
  if (owns("workPlaceVerifiedAtEpochMs")) {
    next.workPlaceVerifiedAtEpochMs =
      typeof prefs.workPlaceVerifiedAtEpochMs === "number" ? prefs.workPlaceVerifiedAtEpochMs : 0;
  }
  if (owns("workPlaceNeedsReselection")) {
    next.workPlaceNeedsReselection = prefs.workPlaceNeedsReselection === true;
  }
  if (owns("weatherLatitude")) {
    next.weatherLatitude =
      typeof prefs.weatherLatitude === "string"
        ? normalizedCoordinateString(prefs.weatherLatitude, -90, 90)
        : "";
  }
  if (owns("weatherLongitude")) {
    next.weatherLongitude =
      typeof prefs.weatherLongitude === "string"
        ? normalizedCoordinateString(prefs.weatherLongitude, -180, 180)
        : "";
  }
  if (owns("weatherLocationLabel")) {
    next.weatherLocationLabel =
      typeof prefs.weatherLocationLabel === "string" ? prefs.weatherLocationLabel.trim() : "";
  }
  if (owns("weatherCustomSlots")) {
    next.weatherCustomSlots =
      typeof prefs.weatherCustomSlots === "string" ? prefs.weatherCustomSlots : "";
  }
  if (owns("preferredDisplayName")) {
    next.preferredDisplayName =
      typeof prefs.preferredDisplayName === "string" ? prefs.preferredDisplayName : "";
  }
  if (owns("webNewsBriefPrimary")) {
    next.newsBriefPrimarySource =
      typeof prefs.webNewsBriefPrimary === "string" ? prefs.webNewsBriefPrimary : "";
  }
  if (owns("webNewsBriefSecondary")) {
    next.newsBriefSecondarySource =
      typeof prefs.webNewsBriefSecondary === "string"
        ? prefs.webNewsBriefSecondary
        : DEFAULTS.newsBriefSecondarySource;
  }
  if (owns("morningBriefEnabled")) {
    next.morningBriefEnabled =
      typeof prefs.morningBriefEnabled === "boolean"
        ? prefs.morningBriefEnabled
        : DEFAULTS.morningBriefEnabled;
  }
  if (owns("morningBriefHour")) {
    next.morningBriefHour =
      typeof prefs.morningBriefHour === "number"
        ? prefs.morningBriefHour
        : DEFAULTS.morningBriefHour;
  }
  if (owns("carouselAutoRotate")) {
    next.carouselAutoRotate =
      typeof prefs.carouselAutoRotate === "boolean"
        ? prefs.carouselAutoRotate
        : DEFAULTS.carouselAutoRotate;
  }
  if (owns("audioPlaybackSpeed")) {
    next.audioPlaybackSpeed =
      typeof prefs.audioPlaybackSpeed === "number" && Number.isFinite(prefs.audioPlaybackSpeed)
        ? Math.min(3, Math.max(0.75, prefs.audioPlaybackSpeed))
        : DEFAULTS.audioPlaybackSpeed;
  }
  if (owns("audioVoiceId")) {
    next.audioVoiceId =
      typeof prefs.audioVoiceId === "string" &&
      ["auto", "leda", "aoede", "charon", "orus"].includes(prefs.audioVoiceId)
        ? prefs.audioVoiceId
        : DEFAULTS.audioVoiceId;
  }
  if (owns("morningBriefQuietHoursEnabled")) {
    next.morningBriefQuietHoursEnabled =
      typeof prefs.morningBriefQuietHoursEnabled === "boolean"
        ? prefs.morningBriefQuietHoursEnabled
        : DEFAULTS.morningBriefQuietHoursEnabled;
  }
  if (owns("hiddenNewsCategories")) {
    next.hiddenNewsCategories =
      typeof prefs.hiddenNewsCategories === "string" ? prefs.hiddenNewsCategories.trim() : "";
  }
  if (owns("hiddenNewsPublishers")) {
    next.hiddenNewsPublishers =
      typeof prefs.hiddenNewsPublishers === "string" ? prefs.hiddenNewsPublishers.trim() : "";
  }
  if (owns("onboardingInterestTags")) {
    next.onboardingInterestTags = normalizedOnboardingInterestTags(prefs.onboardingInterestTags);
  }
  if (owns("hiddenDefaultStockSymbols")) {
    next.hiddenDefaultStockSymbols =
      typeof prefs.hiddenDefaultStockSymbols === "string"
        ? prefs.hiddenDefaultStockSymbols.trim()
        : "";
  }
  if (owns("localCityLabel")) {
    next.localCityLabel =
      typeof prefs.localCityLabel === "string" ? prefs.localCityLabel.trim() : "";
  }
  if (owns("localNewsUserCityCatalogKey")) {
    next.localNewsUserCityCatalogKey =
      typeof prefs.localNewsUserCityCatalogKey === "string"
        ? prefs.localNewsUserCityCatalogKey.trim()
        : "";
  }
  if (owns("localNewsGpsSuggestedCatalogKey")) {
    next.localNewsGpsSuggestedCatalogKey =
      typeof prefs.localNewsGpsSuggestedCatalogKey === "string"
        ? prefs.localNewsGpsSuggestedCatalogKey.trim()
        : "";
  }
  if (owns("localNewsCityUseDeviceLocation")) {
    next.localNewsCityUseDeviceLocation =
      typeof prefs.localNewsCityUseDeviceLocation === "boolean"
        ? prefs.localNewsCityUseDeviceLocation
        : DEFAULTS.localNewsCityUseDeviceLocation;
  }

  next.country = normalizeMarketCountry(next.country, previousCountry);
  const countryChanged = previousCountry !== next.country;
  if (countryChanged) {
    const transitioned = marketRegionPreferenceTransition(next, next.country, previousCountry);
    next.country = transitioned.country;
    next.lang = transitioned.lang;
    if (!owns("newsSource")) next.newsSource = transitioned.newsSource;
    if (!owns("webNewsBriefPrimary")) {
      next.newsBriefPrimarySource = transitioned.newsBriefPrimarySource;
    }
    if (!owns("webNewsBriefSecondary")) {
      next.newsBriefSecondarySource = transitioned.newsBriefSecondarySource;
    }
  } else {
    next.lang = normalizeMarketLanguage(next.lang, next.country);
  }
  next.newsSource = migrateUnavailableNewsSource(next.newsSource, next.country);
  next.newsBriefPrimarySource = migrateUnavailableNewsSource(
    next.newsBriefPrimarySource,
    next.country,
  );
  if (next.newsBriefSecondarySource.trim()) {
    next.newsBriefSecondarySource = migrateUnavailableNewsSource(
      next.newsBriefSecondarySource,
      next.country,
    );
  }
  const remoteOwnsBrief =
    remoteOwns("briefProfilesJson") ||
    remoteOwns("activeBriefProfileId") ||
    remoteOwns("webBriefProfile");
  const canonicalBriefProfiles = briefProfileCollectionFromAndroidSyncPreferences(
    {
      briefProfilesJson: accountSettings.briefProfilesJson,
      activeBriefProfileId: accountSettings.activeBriefProfileId,
    },
    next.country,
  );
  const canonicalBriefProfile =
    canonicalBriefProfiles?.profiles.find(
      (profile) => profile.id === canonicalBriefProfiles.activeProfileId,
    ) ||
    briefProfileFromAndroidSyncPreferences(
      {
        briefProfilesJson: accountSettings.briefProfilesJson,
        activeBriefProfileId: accountSettings.activeBriefProfileId,
      },
      next.country,
    );
  if (countryChanged && !remoteOwnsBrief) {
    const migratedBriefProfiles = migrateBriefProfileCollectionForCountryChange(
      loadBriefProfileCollection(previousCountry),
      next.country,
    );
    next.briefProfilesJson = briefProfilesJsonForAndroid(migratedBriefProfiles);
    next.activeBriefProfileId = migratedBriefProfiles.activeProfileId;
    saveBriefProfileCollection(migratedBriefProfiles);
  } else if (canonicalBriefProfile) {
    next.briefProfilesJson = accountSettings.briefProfilesJson || "";
    next.activeBriefProfileId = canonicalBriefProfile.id;
    if (canonicalBriefProfiles) saveBriefProfileCollection(canonicalBriefProfiles);
    else saveBriefProfile(canonicalBriefProfile);
  } else if (accountSettings.briefProfile) {
    const legacyBriefProfile = normalizeBriefProfile(accountSettings.briefProfile, next.country);
    next.briefProfilesJson = "";
    next.activeBriefProfileId = legacyBriefProfile.id;
    saveBriefProfileCollection({
      profiles: [legacyBriefProfile],
      activeProfileId: legacyBriefProfile.id,
    });
  } else if (
    (owns("briefProfilesJson") && prefs.briefProfilesJson == null) ||
    (owns("activeBriefProfileId") && prefs.activeBriefProfileId == null)
  ) {
    const defaultProfile = normalizeBriefProfile(null, next.country);
    next.briefProfilesJson = "";
    next.activeBriefProfileId = defaultProfile.id;
    saveBriefProfileCollection({
      profiles: [defaultProfile],
      activeProfileId: defaultProfile.id,
    });
  }

  if (countryChanged) next.hiddenDefaultStockSymbols = "";
  savePreferences(next, {
    persistHiddenDefaultStocks: countryChanged || owns("hiddenDefaultStockSymbols"),
  });

  const remoteOwnsCarousel =
    remoteOwns("homeCarouselVisibleIds") || remoteOwns("webHomeCarouselSettings");
  if (countryChanged && !remoteOwnsCarousel) {
    migrateExplicitHomeCarouselDeckForCountry(previousCountry, next.country);
  } else if (accountSettings.hasCanonicalHomeCarouselVisibleIds) {
    if (accountSettings.homeCarouselVisibleIds === null) {
      clearHomeCarouselCustomizationFromSync(next.country);
    } else {
      const canonicalCarousel = homeCarouselSettingsFromVisibleIds(
        accountSettings.homeCarouselVisibleIds,
        next.country,
      );
      if (canonicalCarousel) {
        saveHomeCarouselSettings(canonicalCarousel, next.country);
      }
    }
  } else if (accountSettings.homeCarouselSettings) {
    saveHomeCarouselSettings(
      normalizeHomeCarouselSettings(accountSettings.homeCarouselSettings, next.country),
      next.country,
    );
  }
  return next;
}

export function getSyncSince(): string | null {
  return readAccountScopedStorage(SYNC_SINCE_KEY);
}

export function setSyncSince(iso: string) {
  const current = getSyncSince();
  const currentMs = Date.parse(current || "");
  const nextMs = Date.parse(iso);
  if (Number.isFinite(currentMs) && Number.isFinite(nextMs) && nextMs < currentMs) return;
  writeAccountScopedStorage(SYNC_SINCE_KEY, iso);
}
